import React, { useState, useEffect } from "react";
import MainLayout from "layouts/Main";
import {
  Box,
  Chip,
  IconButton,
  Typography,
  Stack,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Table from "components/Table";
import { grey } from "@mui/material/colors";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectNordPoolSupplyPlan } from "store/selectors";
import { fetchNordPoolSupplyPlan } from "store/appSlice";
import { exportNordPoolSubSupplyPlan } from "api/resources/nordPool";
import { downloadXLSX } from "helpers/excelExport";
import { SupplyPlanContextTypes } from "helpers/enums";
import { ProtectedPaths } from "routes";

const contexts = {
  PRODUCTION_FORECAST: "Production park",
  CONSUMPTION_FORECAST: "Saved sample",
  BASE_LOAD: "Client",
  CLIENT_FORECAST: "Client",
  CLIENT_SUPPLY_PLAN: "Client",
  BATTERY_FORECAST: "Battery",
  IDC_TRANSACTIONS: "Production park",
};

const ViewNordPoolSupplyPlan = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const nordPoolSupplyPlan = useAppSelector(selectNordPoolSupplyPlan);

  useEffect(() => {
    dispatch(fetchNordPoolSupplyPlan(Number(id)));
  }, [dispatch]);

  const handleExportSubSupplyPlan = (id: number, name: string) => {
    exportNordPoolSubSupplyPlan(Number(id))
      .then((result) => {
        downloadXLSX(
          result,
          `sub-supply-plan_${nordPoolSupplyPlan.targetDate}_${name}.xlsx`,
        );
      })
      .catch((error) => console.error(error));
  };

  const handleNavigateToContext = (
    contextType: string,
    contextId: number,
    contextFilter?: number,
  ) => {
    const contextPaths = {
      PRODUCTION_FORECAST: `/${ProtectedPaths.Production}/${contextId}?productionParkIds=${contextFilter}`,
      CONSUMPTION_FORECAST: `/${ProtectedPaths.EditConsumptionForecast}/${contextId}`,
    };

    const newTabUrl = contextPaths[contextType as keyof typeof contextType];

    window.open(newTabUrl, "_blank"); // open in new tab
  };

  const columns = [
    {
      accessorKey: "contextName",
      header: "Name",
    },
    {
      accessorKey: "contextType",
      header: "Context",
      cell: (row: any) => {
        return (
          <Chip
            label={
              contexts[row.row.original.contextType as keyof typeof contexts]
            }
            size="small"
          />
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: (row: any) => {
        return (
          <Chip
            label={
              SupplyPlanContextTypes[
                row.row.original
                  .contextType as keyof typeof SupplyPlanContextTypes
              ]
            }
            size="small"
          />
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created at",
      cell: (row: any) => {
        return dayjs(row.row.original.createdAt).format("DD.MM.YYYY HH:mm");
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (row: any) => {
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                handleNavigateToContext(
                  row.row.original.contextType,
                  row.row.original.contextId,
                  row.row.original.contextFilter,
                )
              }
            >
              Open details
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() =>
                handleExportSubSupplyPlan(
                  row.row.original.id,
                  row.row.original.contextName,
                )
              }
            >
              Export
            </Button>
          </Stack>
        );
      },
    },
  ];

  if (nordPoolSupplyPlan) {
    return (
      <MainLayout>
        <Box sx={{ display: "flex", alignItems: "center", paddingBottom: 2 }}>
          <IconButton sx={{ mr: "1rem" }} onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h5">
            Supply plan
            <span style={{ color: grey[500] }}>
              {` - ${dayjs(nordPoolSupplyPlan.targetDate).format(
                "DD.MM.YYYY",
              )} - ${dayjs(nordPoolSupplyPlan.targetDate).format("dddd")}`}
            </span>
          </Typography>
        </Box>
        <Box>
          <Stack
            direction="row"
            spacing={4}
            padding={2}
            bgcolor={grey[100]}
            maxWidth="fit-content"
          >
            <Typography component="p">
              <strong>Portfolio: </strong>
              {nordPoolSupplyPlan.portfolioName}
            </Typography>
            <Typography component="p">
              <strong>Auction: </strong>
              {nordPoolSupplyPlan.nordpoolAuctionTypeName}
            </Typography>
            <Typography component="p">
              <strong>Status: </strong>
              Generated
            </Typography>
            <Typography component="p">
              <strong>Created at: </strong>
              {dayjs(nordPoolSupplyPlan.createdAt).format("DD.MM.YYYY HH:mm")}
            </Typography>
          </Stack>
        </Box>
        <Box>
          <Stack
            direction="row"
            spacing={4}
            padding={2}
            bgcolor={grey[100]}
            maxWidth="fit-content"
            sx={{ mt: 2 }}
          >
            <Typography component="p">
              <strong>Min (MW): </strong>
              {nordPoolSupplyPlan.currentAuctionMetrics.min}
              {nordPoolSupplyPlan.previousAuctionMetrics.min &&
                " (" + nordPoolSupplyPlan.previousAuctionMetrics.min + ")"}
            </Typography>
            <Typography component="p">
              <strong>Max (MW): </strong>
              {nordPoolSupplyPlan.currentAuctionMetrics.max}
              {nordPoolSupplyPlan.previousAuctionMetrics.max &&
                " (" + nordPoolSupplyPlan.previousAuctionMetrics.max + ")"}
            </Typography>
            <Typography component="p">
              <strong>Sum Min (MW): </strong>
              {nordPoolSupplyPlan.currentAuctionMetrics.sumMin}
              {nordPoolSupplyPlan.previousAuctionMetrics.sumMin &&
                " (" + nordPoolSupplyPlan.previousAuctionMetrics.sumMin + ")"}
            </Typography>
            <Typography component="p">
              <strong>Sum Max (MW): </strong>
              {nordPoolSupplyPlan.currentAuctionMetrics.sumMax}
              {nordPoolSupplyPlan.previousAuctionMetrics.sumMax &&
                " (" + nordPoolSupplyPlan.previousAuctionMetrics.sumMax + ")"}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ mt: 6 }}>
          <Typography
            component="h5"
            variant="h5"
            fontSize={18}
            fontWeight={500}
          >
            Sub-supply plans:
          </Typography>
          <Table
            data={nordPoolSupplyPlan.subPlans}
            columns={columns}
            isFetching={!nordPoolSupplyPlan.subPlans}
            pageCount={1}
            page={setCurrentPage}
            searchLabel="Search portfolios"
          />
        </Box>
      </MainLayout>
    );
  }
  return null;
};

export default ViewNordPoolSupplyPlan;
